// Pre-trust + rate-limit filter for incoming Web Push call rings.
//
// Two threats this defends against:
//
//   1. Server-driven unknown-caller doxxing. A hostile server can push a
//      ringing payload claiming any callerUserId. We never auto-fetch the
//      encrypted call descriptor for a caller we have no session with —
//      the user gets an "Unknown caller" notification (never a server-
//      supplied string) so they can decline without the app revealing any
//      peer state to the server.
//
//   2. Web Push flooding. A peer (compromised or hostile) can push more
//      rings than is plausible to drain battery and grief the user. Past
//      N=5 rings inside a 30-second window we drop subsequent pushes.
//      On web, the caller synthesises an immediate silent "ended"
//      notification to satisfy any pending OS contract (iOS equivalent:
//      synthesise an immediate "ended" CallKit report).
//
// Stateful: holds the known-peers set and per-caller sliding window.
// Clock is injectable so tests can advance it deterministically.
//
// iOS counterpart: kaether_for_ios/Stoat/E2EE/CallRingingFilter.swift

import type { CallRingingPushPayload } from "./CallRingingPayload";

// ---------------------------------------------------------------------------
// Decision type
// ---------------------------------------------------------------------------

/**
 * Decision returned by `CallRingingFilter.evaluate`. The caller MUST act on
 * all three cases to honour the Web Push / Notification API contract.
 */
export interface CallRingingFilterDecision {
  /**
   * True iff the push should drive the normal incoming-call flow: fetch the
   * descriptor, transition through `.incoming → .ringing`, and show the user
   * an Accept/Decline notification.
   */
  readonly allowed: boolean;

  /**
   * Human-readable reason for `allowed=false`. Surfaced in diagnostics.
   */
  readonly reason: string;

  /**
   * When `allowed=false`, tells the caller how to fulfil the notification
   * contract:
   *   - `true`: show a notification with "Unknown caller" (never a peer-
   *     supplied string); do NOT auto-fetch the descriptor. User declines via
   *     the notification action or dismisses.
   *   - `false`: rate-limit case. The peer is known but is spamming rings.
   *     Silently discard — no notification flashes up. On iOS the caller
   *     synthesises an immediate "ended" CallKit report; on web no action is
   *     required (there is no OS enforcement timer to satisfy).
   */
  readonly synthesiseUnknownCallerUI: boolean;
}

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

export class CallRingingFilter {
  /** Window length in seconds for the per-caller ring counter. */
  readonly windowSeconds: number;

  /** Max rings from one caller within `windowSeconds` before dropping. */
  readonly maxRingsPerWindow: number;

  private readonly nowFn: () => number;

  /** Set of known peer user-ids. Updated by the E2EE manager layer. */
  private knownPeers: Set<string> = new Set();

  /**
   * Per-caller timeline of ring timestamps within the active window.
   * Trimmed lazily on every evaluate call.
   */
  private ringTimestamps: Map<string, number[]> = new Map();

  constructor(opts?: {
    now?: () => number;
    windowSeconds?: number;
    maxRingsPerWindow?: number;
  }) {
    this.nowFn = opts?.now ?? (() => Math.floor(Date.now() / 1000));
    this.windowSeconds = opts?.windowSeconds ?? 30;
    this.maxRingsPerWindow = opts?.maxRingsPerWindow ?? 5;
  }

  /**
   * Update the known-peer set. Called by the E2EE manager whenever sessions
   * are added/removed so the filter has the source of truth without reading
   * the session store directly.
   */
  setKnownPeers(ids: ReadonlySet<string>): void {
    this.knownPeers = new Set(ids);
  }

  /**
   * Evaluate a Web Push call payload against the known-peer set and the
   * per-caller rate limit. Records the ring into the sliding window when
   * `allowed=true`. The caller is responsible for acting on the decision.
   */
  evaluate(payload: CallRingingPushPayload, at: number): CallRingingFilterDecision {
    // Pre-trust filter: caller must already be a known peer.
    if (!this.knownPeers.has(payload.callerUserId)) {
      return {
        allowed: false,
        reason: "unknown peer",
        synthesiseUnknownCallerUI: true,
      };
    }

    // Rate-limit filter: prune the per-caller window, then count.
    const cutoff = at - this.windowSeconds;
    const existing = this.ringTimestamps.get(payload.callerUserId) ?? [];
    const trimmed = existing.filter((t) => t > cutoff);

    if (trimmed.length >= this.maxRingsPerWindow) {
      // Persist the pruned window so we don't accumulate dead entries.
      this.ringTimestamps.set(payload.callerUserId, trimmed);
      return {
        allowed: false,
        reason: "rate limited",
        synthesiseUnknownCallerUI: false,
      };
    }

    trimmed.push(at);
    this.ringTimestamps.set(payload.callerUserId, trimmed);
    return { allowed: true, reason: "ok", synthesiseUnknownCallerUI: false };
  }

  /**
   * Drop all per-caller window state. Used by tests; production callers
   * have no reason to call this — the window prunes itself on evaluate.
   */
  reset(): void {
    this.ringTimestamps.clear();
  }
}
